import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import * as CryptoJS from 'crypto-js';
import * as XLSX from 'xlsx';
import { getDepreciationIndAsLogGrouped, getDepreciationIndAsLogBatchDetails } from '../../../services/asset.service';
import { getYearOfAdditionList } from '../../../services/master.service';
import '../AssetInfo/AssetList.css';
import './UploadDepreciation.css';

const monthsList = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
];

function DepreciationIndAsLogReport() {
    const [loading, setLoading] = useState(false);
    const [groupedLogs, setGroupedLogs] = useState([]);
    const [errorMsg, setErrorMsg] = useState('');
    const [yearList, setYearList] = useState([]);

    // Filters
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [batchIdInput, setBatchIdInput] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Detail Modal States
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [selectedBatchId, setSelectedBatchId] = useState('');
    const [detailLoading, setDetailLoading] = useState(false);
    const [batchDetails, setBatchDetails] = useState([]);

    const fetchGroupedLogs = () => {
        setLoading(true);
        setErrorMsg('');

        const param_data = {
            company_id: sessionStorage.getItem('company_id') || null,
            financialYear: selectedYear || null,
            month: selectedMonth || null,
            batchId: batchIdInput.trim() || null,
            status: selectedStatus === 'All' ? null : selectedStatus,
            fromDate: fromDate || null,
            toDate: toDate || null
        };

        let EncryptData = CryptoJS.AES.encrypt(
            JSON.stringify({
                authToken: sessionStorage.getItem('LoggedInToken'),
                param_data: param_data
            }),
            process.env.REACT_APP_ENCRYPT_SECRET_KEY
        ).toString();

        const payload = {
            token: EncryptData
        };

        getDepreciationIndAsLogGrouped(payload)
            .then(res => {
                if (res.data && res.data.success) {
                    setGroupedLogs(res.data.data || []);
                } else if (res.data && res.data.data) {
                    setGroupedLogs(res.data.data);
                } else {
                    setGroupedLogs([]);
                    if (res.data && res.data.message) setErrorMsg(res.data.message);
                }
            })
            .catch(err => {
                console.error("Error fetching grouped IndAS logs:", err);
                setErrorMsg(err.message || "Failed to load depreciation IndAS log report.");
                setGroupedLogs([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchGroupedLogs();

        let param_data = {
            company_id: sessionStorage.getItem('company_id') || null
        };
        let EncryptData = CryptoJS.AES.encrypt(
            JSON.stringify({
                authToken: sessionStorage.getItem('LoggedInToken'),
                param_data: param_data
            }),
            process.env.REACT_APP_ENCRYPT_SECRET_KEY
        ).toString();

        getYearOfAdditionList({ token: EncryptData })
            .then(res => {
                if (res?.data?.data?.length) {
                    let list = res.data.data;
                    list = list.filter(item => {
                        if (item.show_in_depreciation === undefined || item.show_in_depreciation === null) return false;
                        const val = String(item.show_in_depreciation).toLowerCase().trim();
                        return val === 'yes' || val === '1' || val === 'true';
                    });
                    list.sort((a, b) => {
                        const yrA = parseInt(a.year_of_addition || a.year_short_name || 0, 10);
                        const yrB = parseInt(b.year_of_addition || b.year_short_name || 0, 10);
                        return yrB - yrA;
                    });
                    setYearList(list);
                }
            })
            .catch(err => console.error("Error fetching year of addition list:", err));
    }, []);

    const handleApplyFilter = () => {
        setPage(1);
        fetchGroupedLogs();
    };

    const handleResetFilter = () => {
        setSelectedYear('');
        setSelectedMonth('');
        setBatchIdInput('');
        setSelectedStatus('All');
        setFromDate('');
        setToDate('');
        setPage(1);
        setTimeout(() => {
            fetchGroupedLogs();
        }, 0);
    };

    const handleViewClick = (batchId) => {
        setSelectedBatchId(batchId);
        setOpenDetailModal(true);
        setDetailLoading(true);
        setBatchDetails([]);

        const param_data = {
            company_id: sessionStorage.getItem('company_id') || null,
            batch_id: batchId
        };

        let EncryptData = CryptoJS.AES.encrypt(
            JSON.stringify({
                authToken: sessionStorage.getItem('LoggedInToken'),
                param_data: param_data
            }),
            process.env.REACT_APP_ENCRYPT_SECRET_KEY
        ).toString();

        const payload = {
            token: EncryptData
        };

        getDepreciationIndAsLogBatchDetails(payload)
            .then(res => {
                if (res.data && res.data.success) {
                    setBatchDetails(res.data.data || []);
                } else if (res.data && res.data.data) {
                    setBatchDetails(res.data.data);
                } else {
                    setBatchDetails([]);
                }
            })
            .catch(err => {
                console.error("Error fetching IndAS batch details:", err);
                setBatchDetails([]);
            })
            .finally(() => {
                setDetailLoading(false);
            });
    };

    const handleDownloadExcel = (batchId, detailsData = null) => {
        const downloadData = (dataToExport) => {
            if (!dataToExport || dataToExport.length === 0) {
                alert("No records found to export for Batch ID: " + batchId);
                return;
            }

            const formattedExport = dataToExport.map(row => ({
                'Tag': row.tag || '',
                'Parent Tag': row.parent_tag || '-',
                'Den No': row.den_no || '',
                'Batch ID': row.batch_id || batchId,
                'Days': row.days ?? '',
                'Year': row.year ?? '',
                'Month': row.month ? (monthsList.find(m => String(m.value) === String(row.month))?.label || row.month) : '',
                'Asset OB': parseFloat(row.asset_ob || 0),
                'Added Value': parseFloat(row.asset_added_value || 0),
                'Adjustment Value': parseFloat(row.asset_adjustment_value || 0),
                'Transferred Value': parseFloat(row.asset_transfered || 0),
                'Deleted Value': parseFloat(row.asset_deleted_value || 0),
                'Asset CB': parseFloat(row.asset_cb || 0),
                'Dep. OB': parseFloat(row.depreciation_ob || 0),
                'Dep. Added': parseFloat(row.depreciation_added_value || 0),
                'Dep. Adjusted': parseFloat(row.depreciation_adjusted_value || 0),
                'Dep. Transferred': parseFloat(row.depreciation_transfered || 0),
                'Dep. Deleted': parseFloat(row.depreciation_deleted_value || 0),
                'Dep. CB': parseFloat(row.depreciation_cb || 0),
                'WDV OB': parseFloat(row.wdv_ob || 0),
                'WDV CB': parseFloat(row.wdv_cb || 0)
            }));

            const worksheet = XLSX.utils.json_to_sheet(formattedExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Depreciation IndAS Log");
            XLSX.writeFile(workbook, `Depreciation_IndAS_Log_Batch_${batchId}.xlsx`);
        };

        if (detailsData && detailsData.length > 0) {
            downloadData(detailsData);
        } else {
            const param_data = {
                company_id: sessionStorage.getItem('company_id') || null,
                batch_id: batchId
            };

            let EncryptData = CryptoJS.AES.encrypt(
                JSON.stringify({
                    authToken: sessionStorage.getItem('LoggedInToken'),
                    param_data: param_data
                }),
                process.env.REACT_APP_ENCRYPT_SECRET_KEY
            ).toString();

            const payload = {
                token: EncryptData
            };

            getDepreciationIndAsLogBatchDetails(payload)
                .then(res => {
                    const data = (res.data && res.data.success) ? res.data.data : (res.data?.data || []);
                    downloadData(data);
                })
                .catch(err => {
                    console.error("Download error:", err);
                    alert("Failed to download IndAS excel data.");
                });
        }
    };

    const getMonthName = (monthVal) => {
        const found = monthsList.find(m => String(m.value) === String(monthVal));
        return found ? found.label : monthVal;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('en-GB'); // DD/MM/YYYY
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleTimeString('en-US', { hour12: false });
    };

    // Client-side pagination for grouped logs
    const totalRecords = groupedLogs.length;
    const startIndex = (page - 1) * pageSize;
    const paginatedGroupedLogs = groupedLogs.slice(startIndex, startIndex + pageSize);

    return (
        <div className="al-page">
            {/* Header */}
            <div className="al-page-header">
                <div className="al-page-header-left">
                    <div className="al-page-header-icon" style={{ backgroundColor: '#f0fdf4', color: '#16a34a' }}>
                        <i className="bi bi-journal-check" />
                    </div>
                    <div className="al-page-header-text">
                        <h5 className="al-page-title">Depreciation IndAS Log Report</h5>
                        <p className="al-page-subtitle">Grouped IndAS log summary by Batch ID (company_indas_depreciation_log)</p>
                    </div>
                </div>
                {!loading && (
                    <div className="al-record-badge">
                        <i className="bi bi-layers-fill" />
                        <strong>{totalRecords.toLocaleString('en-IN')}</strong>&nbsp;Batches Found
                    </div>
                )}
            </div>

            {errorMsg && (
                <div className="ud-alert ud-alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill" /> {errorMsg}
                </div>
            )}

            {/* Filter Search Criteria Panel */}
            <div className="al-filter-card">
                <div className="al-filter-card-header">
                    <span className="al-filter-card-title">
                        <i className="bi bi-funnel-fill" /> Search Criteria
                    </span>
                </div>
                <div className="al-filter-card-body">
                    {/* Row 1: Dropdowns & Inputs */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        {/* Financial Year Dropdown */}
                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-calendar-range" /> Financial Year
                            </label>
                            <select
                                className="al-form-select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            >
                                <option value="">Select Financial Year</option>
                                {yearList.map((item, idx) => (
                                    <option key={item.id || idx} value={item.year_short_name}>
                                        {item.year_of_addition}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Month Wise Dropdown */}
                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-calendar-month" /> Month Wise
                            </label>
                            <select
                                className="al-form-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            >
                                <option value="">All Months</option>
                                {monthsList.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Batch ID Search Input */}
                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-hash" /> Batch ID
                            </label>
                            <input
                                type="text"
                                className="al-form-input"
                                placeholder="Enter Batch ID..."
                                value={batchIdInput}
                                onChange={(e) => setBatchIdInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                            />
                        </div>

                        {/* Status Dropdown */}
                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-check-circle" /> Status
                            </label>
                            <select
                                className="al-form-select"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                            >
                                <option value="All">All Statuses</option>
                                <option value="Success">Success</option>
                                <option value="Failed">Failed</option>
                            </select>
                        </div>
                    </div>

                    {/* Row 2: Date Filters & Action Buttons */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', alignItems: 'flex-end' }}>
                        {/* Generated Date From */}
                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-calendar3" /> Generated From
                            </label>
                            <input
                                type="date"
                                className="al-form-input"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>

                        {/* Generated Date To */}
                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-calendar3" /> Generated To
                            </label>
                            <input
                                type="date"
                                className="al-form-input"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', height: '38px' }}>
                            <button
                                type="button"
                                className="al-btn al-btn-primary"
                                onClick={handleApplyFilter}
                            >
                                <i className="bi bi-search" /> Apply Filter
                            </button>
                            <button
                                type="button"
                                className="al-btn al-btn-secondary"
                                onClick={handleResetFilter}
                            >
                                <i className="bi bi-arrow-counterclockwise" /> Reset Filter
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Grouped Table List */}
            <div className="al-grid-card">
                <div className="al-grid-card-header">
                    <span className="al-grid-card-title">
                        <i className="bi bi-list-task" /> Depreciation IndAS Log Batches
                    </span>
                    {!loading && totalRecords > 0 && (
                        <span className="al-showing-badge">
                            Showing <strong>{startIndex + 1}</strong> to <strong>{Math.min(startIndex + pageSize, totalRecords)}</strong> of <strong>{totalRecords.toLocaleString('en-IN')}</strong>
                        </span>
                    )}
                </div>

                <div className="al-filter-card-body" style={{ padding: '20px' }}>
                    <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <Table aria-label="grouped indas log table">
                            <TableHead style={{ backgroundColor: '#f9fafb' }}>
                                <TableRow>
                                    <TableCell style={{ fontWeight: '600', color: '#374151', width: '70px' }}>Sl No</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Batch ID</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Month</TableCell>
                                    <TableCell align="center" style={{ fontWeight: '600', color: '#374151' }}>Status</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Date</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Time</TableCell>
                                    <TableCell align="right" style={{ fontWeight: '600', color: '#374151' }}>No of TAGs</TableCell>
                                    <TableCell align="center" style={{ fontWeight: '600', color: '#374151' }}>Time taken</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Generated By</TableCell>
                                    <TableCell align="center" style={{ fontWeight: '600', color: '#374151' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" style={{ padding: '40px 0', color: '#6b7280' }}>
                                            <div className="spinner-border spinner-border-sm text-success me-2" role="status" />
                                            Loading grouped IndAS depreciation logs...
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedGroupedLogs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" style={{ padding: '40px 0', color: '#6b7280' }}>
                                            No IndAS depreciation log batches found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedGroupedLogs.map((row, idx) => (
                                        <TableRow key={row.batch_id + '-' + idx} hover>
                                            <TableCell style={{ color: '#4b5563' }}>{startIndex + idx + 1}</TableCell>
                                            <TableCell style={{ fontWeight: '600', color: '#111827' }}>
                                                <code>{row.batch_id}</code>
                                            </TableCell>
                                            <TableCell>{getMonthName(row.month)} - {row.year ? String(row.year).slice(-2) : ''}</TableCell>
                                            <TableCell align="center">
                                                <span style={{
                                                    padding: '4px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    backgroundColor: (row.status === 'Success' || String(row.status) === '1') ? '#e2f9e9' : '#fee2e2',
                                                    color: (row.status === 'Success' || String(row.status) === '1') ? '#198754' : '#dc2626',
                                                    border: `1px solid ${(row.status === 'Success' || String(row.status) === '1') ? '#c3e6cb' : '#fecaca'}`
                                                }}>
                                                    {(String(row.status) === '1' || row.status === 'Success') ? 'Success' : (String(row.status) === '0' || row.status === 'Failed') ? 'Failed' : (row.status || 'Success')}
                                                </span>
                                            </TableCell>
                                            <TableCell>{formatDate(row.created_at)}</TableCell>
                                            <TableCell>{formatTime(row.created_at)}</TableCell>
                                            <TableCell align="right" style={{ fontWeight: '600' }}>{row.no_of_tags || 0}</TableCell>
                                            <TableCell align="center" style={{ color: '#6b7280' }}>{row.time_taken || 'N/A'}</TableCell>
                                            <TableCell>{row.created_by || 'System'}</TableCell>
                                            <TableCell align="center">
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                                                    <Tooltip title="View Batch Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewClick(row.batch_id)}
                                                            style={{
                                                                backgroundColor: '#0284c7',
                                                                color: '#ffffff',
                                                                borderRadius: '6px',
                                                                width: '34px',
                                                                height: '34px'
                                                            }}
                                                        >
                                                            <i className="bi bi-eye-fill" style={{ fontSize: '1rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Download Excel">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDownloadExcel(row.batch_id)}
                                                            style={{
                                                                backgroundColor: '#ffffff',
                                                                color: '#16a34a',
                                                                border: '1px solid #16a34a',
                                                                borderRadius: '6px',
                                                                width: '34px',
                                                                height: '34px'
                                                            }}
                                                        >
                                                            <i className="bi bi-file-earmark-excel-fill" style={{ fontSize: '0.95rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <div className="al-pagination-bar">
                        <div className="al-page-size-label">
                            {totalRecords > 0 ? (
                                `Showing ${startIndex + 1} to ${Math.min(startIndex + pageSize, totalRecords)} of ${totalRecords} entries`
                            ) : (
                                "Showing 0 to 0 of 0 entries"
                            )}
                        </div>
                        <div className="al-page-size-row" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Pagination
                                count={Math.ceil(totalRecords / pageSize) || 1}
                                page={page}
                                onChange={(event, value) => setPage(value)}
                                color="success"
                                size="small"
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="al-page-size-label">Page Size:</span>
                                <select
                                    className="al-form-select"
                                    style={{ width: '80px', height: '32px', padding: '0 8px', backgroundColor: '#fff' }}
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Detailed View Modal */}
            <Dialog
                open={openDetailModal}
                onClose={() => setOpenDetailModal(false)}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    style: { borderRadius: '12px', padding: '8px' }
                }}
            >
                <DialogTitle style={{ fontWeight: '700', color: '#15803d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                        <i className="bi bi-table me-2" /> Depreciation IndAS Log Records &mdash; Batch ID: <code>{selectedBatchId}</code>
                    </span>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleDownloadExcel(selectedBatchId, batchDetails)}
                        startIcon={<i className="bi bi-file-earmark-excel-fill" />}
                        style={{ textTransform: 'none', borderRadius: '6px' }}
                        disabled={detailLoading || batchDetails.length === 0}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"></path>
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"></path>
                        </svg>
                    </Button>
                </DialogTitle>
                <DialogContent dividers style={{ padding: '16px' }}>
                    {detailLoading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: '16px' }}>
                            <CircularProgress size={40} style={{ color: '#16a34a' }} />
                            <span style={{ fontSize: '0.9rem', color: '#4b5563' }}>Loading detailed IndAS depreciation log records...</span>
                        </div>
                    ) : batchDetails.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
                            No log records found for Batch ID: {selectedBatchId}
                        </div>
                    ) : (
                        <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e5e7eb', maxHeight: '550px' }}>
                            <Table stickyHeader size="small" aria-label="indas details table">
                                <TableHead style={{ backgroundColor: '#f3f4f6' }}>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Tag</TableCell>
                                        <TableCell style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Parent Tag</TableCell>
                                        <TableCell style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Den No</TableCell>
                                        <TableCell style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Batch ID</TableCell>
                                        <TableCell style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Days</TableCell>
                                        <TableCell style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Year</TableCell>
                                        <TableCell style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Month</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Asset OB</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Added Value</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Adjustment Value</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Transferred Value</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Deleted Value</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Asset CB</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Dep. OB</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Dep. Added</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Dep. Adjusted</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Dep. Transferred</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Dep. Deleted</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>Dep. CB</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>WDV OB</TableCell>
                                        <TableCell align="right" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>WDV CB</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {batchDetails.map((row) => (
                                        <TableRow key={row.id || row.tag} hover>
                                            <TableCell style={{ fontWeight: '600' }}>{row.tag}</TableCell>
                                            <TableCell>{row.parent_tag || '-'}</TableCell>
                                            <TableCell>{row.den_no || ''}</TableCell>
                                            <TableCell><code>{row.batch_id}</code></TableCell>
                                            <TableCell>{row.days}</TableCell>
                                            <TableCell>{row.year}</TableCell>
                                            <TableCell>{getMonthName(row.month)}</TableCell>
                                            <TableCell align="right">{Number(row.asset_ob || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.asset_added_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.asset_adjustment_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.asset_transfered || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.asset_deleted_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right" style={{ fontWeight: '600', backgroundColor: '#f0fdf4' }}>{Number(row.asset_cb || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.depreciation_ob || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.depreciation_added_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.depreciation_adjusted_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.depreciation_transfered || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.depreciation_deleted_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right" style={{ fontWeight: '600', backgroundColor: '#fff7ed' }}>{Number(row.depreciation_cb || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(row.wdv_ob || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right" style={{ fontWeight: '600', backgroundColor: '#f0f9ff' }}>{Number(row.wdv_cb || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px' }}>
                    <Button onClick={() => setOpenDetailModal(false)} variant="outlined" style={{ textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default DepreciationIndAsLogReport;
